const requestSchema = [
  {
    name: "createdAt",
    type: "number",
    desc: "Unix timestamp of when the alert was created.",
    required: true
  },
  {
    name: "alertId",
    type: "string",
    desc: "The reference id of the alert.",
    required: true
  },
  {
    name: "active",
    type: "bool",
    desc: "If the alert not is in the past AND if the alert is not canceled.",
    required: true
  },
  {
    name: "canceled",
    type: "bool",
    desc: "If the alert was canceled.",
    required: true
  },
  {
    name: "canceledAtUnix",
    type: "number",
    desc: "If the alert was canceled then this is the unix timestamp of when canceled.",
    required: false
  },
  {
    name: "campgrounds",
    type: "array",
    desc: "An array of campground ids that Campflare is looking for availabilities for.",
    required: true
  },
  {
    name: "dates",
    type: "array",
    desc: "The starting dates to look for availabilities for, formatted in <c>YYYY-MM-DD</c> in the timezone of the campground.",
    required: true
  },
  {
    name: "minimumNumberOfNights",
    type: "number",
    desc: "The minimum number of consecutive nights starting on a date needed to trigger a notification.",
    required: true
  },
  {
    name: "campsiteTypeFilter",
    type: "array",
    desc: "A list of campsite types to filter upon.",
    required: false
  },
  {
    name: "minimumRigLengthFt",
    type: "number",
    desc: "The minimum number of ft of a rig that a campsite can host.",
    required: false
  },
  {
    name: "metadata",
    type: "map",
    desc: "A mapping of any data you wish to associate with the alert. Limited to 2kb.",
    required: false
  },
  {
    name: "notifications",
    type: "array",
    desc: "A list of the notifications sent to the alert.",
    required: true,
    attributes: [
      {
        name: "notificationId",
        type: "string",
        desc: "The id of the notification.",
        required: true
      },
      {
        name: "sentAtUnix",
        type: "number",
        desc: "The unix timestamp of when the notification was sent to the webhook.",
        required: true
      },
      {
        name: "site",
        type: "string",
        desc: "The site number/name that the availability was detected at.",
        required: true
      },
      {
        name: "alertId",
        type: "string",
        desc: "The reference id of the alert.",
        required: true
      },
      {
        name: "numberOfNights",
        type: "number",
        desc: "The number of nights of availability.",
        required: true
      },
      {
        name: "startDate",
        type: "string",
        desc: "What date the availability starts at, in <c>YYYY-MM-DD</c> format.",
        required: true
      },
      {
        name: "campground",
        type: "string",
        desc: "The id of the campground the availability was detected at.",
        required: true
      },
      {
        name: "webhookResponseCode",
        type: "number",
        desc: "The response code that the webhook sent back after sending the notification.",
        required: true
      },
      {
        name: "reserveLink",
        type: "string",
        desc: "The link to book the campground.",
        required: true
      }
    ]
  },
]


const campgroundSchema = [
  {
    name: "id",
    type: "string",
    desc: "Id of the campground",
    required: true
  },
  {
    name: "name",
    type: "string",
    desc: "Name of the campground",
    required: true
  },
  {
    name: "shortDescription",
    type: "string",
    desc: "A short summary of the campground. Usually a couple sentences.",
    required: false
  },
  {
    name: "bookingLink",
    type: "string",
    desc: "The link to where the campground can be booked/reserved",
    required: false
  },
  {
    name: "numberOfCampsites",
    type: "number",
    desc: "Number of campsites in this campground",
    required: true
  },
  {
    name: "campsiteTypes",
    type: "array",
    desc: "Different types of campsites in the campground. Note that these types are only unique and specific to a singular campground, since they are provided by the associated booking website.",
    required: false,
    attributes: [
      {
        name: "id",
        type: "string",
        desc: "Id of the campsite type",
        required: true
      },
      {
        name: "name",
        type: "string",
        desc: "Readable name of campsite type",
        required: true
      },
      {
        name: "numberOfCampsites",
        type: "number",
        desc: "Number of campsites in this campground that have this type",
        required: true
      }
    ]
  },
]

const campgroundSearchSchema = [
  {
    name: "results",
    type: "array",
    desc: "An array of campgrounds that match the search query.",
    required: true,
    attributes: campgroundSchema,
  }
]

const exampleCampground = {
  "id": "232447",
  "name": "Upper Pines",
  "shortDescription": "Upper Pines Campground in Yosemite National Park is a popular destination for activities like hiking, rafting, biking and more. Surrounded by breathtaking natural features, the campground offers paved roads, flush toilets, drinking water, a picnic table, fire ring, and food storage locker for each campsite.",
  "numberOfCampsites": 236,
  "bookingLink": "https://campflare.com/r/232447",
  "campsiteTypes": [
    {
      id: "rv",
      name: "RV Site",
      numberOfCampsites: 38,
    },
    {
      id: "tent-only",
      name: "Tent Only",
      numberOfCampsites: 12,
    }
  ]
}

export const state = {
  groups: [
    {
      title: "Getting Started",
      id: "introduction1",
      desc: "This guide will help you start using the Campflare API.",
      sections: [
        {
          title: "Introduction",
          id: "introduction1",
          desc: `The Campflare API was made to be simple and robust. No client libraries are required and every operation can be performed via a simple HTTPS call. The following endpoint is used throughout the entire API.`,
          blocks: [
            {
              type: "code",
              code: "https://api.campflare.com/v1/{endpoint}",
              lang: "html",
              title: "v1 API Endpoint"
            },
          ]
        },
        {
          title: "Authorization",
          id: "introd35uction1",
          desc: `To access the API, one must obtain an API key by signing into their <a href="/platform/account">Campflare Developer Dashboard</a>. All requests to the API must be sent with an <c>authorization</c> header with the value set to your API key. If this header is not present, an <c>HTTP 401</c> status code will be returned. You can confirm your API key works with this following endpoint.`,
          blocks: [
            {
              type: "request",
              code: "https://campflare.com/v1/ping",
              lang: "html",
              title: "Confirming your API Key",
              request: {
                url: "https://campflare.com/v1/ping",
                method: "GET",
                headers: {
                  "accept": "application/json",
                  "authorization": "<your api key>"
                }
              }
            },
            {
              title: "Response",
              type: "code",
              code: {
                ping: "pong"
              },
              lang: "json",
            }
          ]
        },
        {
          title: "Errors",
          id: "intro333duction2",
          desc: "All successful API calls will return a <c>HTTP 200</c> status code. All failed requests will return an <c>HTTP 400</c> status code. Unauthorized requests will have a <c>HTTP 401</c> status code. <br><br> All failed requests will have two values, <c>error</c> and <c>errorCode</c>. For example, internal server errors have the error code <c>1</c>. Expect a detailed list of error codes soon.",
          blocks: [
            {
              title: "Error Response",
              type: "code",
              code: {
                error: "The campground id provided is not valid.",
                errorCode: 4,
              },
              lang: "json",
            }
          ]
        },
      ],
    },
    {
      title: "Campgrounds",
      id: "campgrounds",
      desc: "This section details Campflare's campground data.",
      sections: [
        {
          title: "Fetching",
          id: "introewljkfuction",
          desc: "This section details how to fetch all data associated with Campgrounds.",
          api: {
            requests: [
              {
                title: "Getting a Campground",
                desc: "Simply returns metadata associated with a singular campground. Campgrounds that are available on <a href='https://www.recreation.gov/'>recreation.gov</a> use the same ids, which can be found using their <a href='https://ridb.recreation.gov/docs'>RIDB Api</a>.",
                request: {
                  parameters: [
                    {
                      name: "id",
                      type: "string",
                      desc: "The id of the campground to fetch",
                      required: true
                    }
                  ],
                  url: "https://api.campflare.com/v1/campgrounds/get?id={campgroundId}",
                  method: "GET",
                  headers: {
                    "accept": "application/json",
                    "authorization": "<your api key>"
                  }
                },
                response: exampleCampground,
                attributes: campgroundSchema
              },
              {
                title: "Searching for Campgrounds",
                desc: "This endpoint takes a query string and returns campgrounds relevant to such string. The name, park name, location, and the description of the campground will all be taken into account.",
                request: {
                  parameters: [
                    {
                      name: "searchQuery",
                      type: "string",
                      desc: "The search query. This will match all relevant data associated with a campground (name, park name, location, etc).",
                      required: true
                    }
                  ],
                  url: "https://api.campflare.com/v1/campgrounds/search?q={searchQuery}",
                  method: "GET",
                  headers: {
                    "accept": "application/json",
                    "authorization": "<your api key>"
                  }
                },
                response: {
                  results: [
                    exampleCampground,
                  ]
                },
                attributes: campgroundSearchSchema
              },
            ]
          }
        }
      ],
    },
    {
      title: "Availability Alerts",
      desc: "The following sections detail how to interact with Campflare's availability alerts programmatically. What is an availability alert on a technical level? Each alert is essentially a set of constraints on top of the state of all campgrounds. Campflare continuously updates this state. When an alert switches from unsatisfied to satisfied, a notification will be sent to the configured webhook.",
      id: "availability",
      sections: [
        {
          title: "Alert Management",
          id: "availability_req",
          desc: "This section describes how create, retrieve, and cancel availability alerts. The next section covers how to receive the notifications.",
          api: {
            requests: [
              {
                title: "Creating an Alert",
                desc: "This endpoint creats an Alert, and immediately starts it. The Alert will run indefinitely until canceled, or until all of the dates are in the past.",
                request: {
                  parameters: [
                    {
                      name: "campgroundIds",
                      type: "array",
                      desc: "An array of campground ids that Campflare will look for availabilities for.",
                      required: true
                    },
                    {
                      name: "dates",
                      type: "string",
                      desc: "The starting dates to look for availabilities for, formatted in <c>YYYY-MM-DD</c> in the timezone of the campground.",
                      required: true
                    },
                    {
                      name: "minimumNumberOfNights",
                      type: "number",
                      desc: "The minimum number of consecutive nights starting on a date needed to trigger a notification.",
                      required: true
                    },
                    {
                      name: "campsiteTypeFilter",
                      type: "array",
                      desc: "A list of campsite types to filter upon. For example, a user may want to filter by RV Sites only. These types can be be found for each campground via the <c>/campground/get</c> endpoint.",
                      required: false
                    },
                    {
                      name: "minimumRigLengthFt",
                      type: "number",
                      desc: "The minimum number of ft of a rig that a campsite can host.",
                      required: false
                    },
                    {
                      name: "metadata",
                      type: "map",
                      desc: "A mapping of any data you wish to associate with the alert which will be sent with each alert to the webhook.  Limited to 2kb.",
                      required: false
                    }
                  ],
                  url: "https://api.campflare.com/v1/alerts/create",
                  method: "POST",
                  headers: {
                    "accept": "application/json",
                    "authorization": "<your api key>",
                    "Content-Type": "application/json"
                  },
                  body: {
                    "campgrounds": [
                      "38226",
                      "94836",
                      "48327"
                    ],
                    "dates": [
                      "2022-06-12",
                      "2022-06-13"
                    ],
                    "minimumNumberOfNights": 2,
                    "campsiteTypeFilter": [
                      "rv_site"
                    ],
                    "minimumRigLengthFt": 30,
                    "metadata": {
                      "anything": 123,
                      "you": "want",
                      "here": []
                    }
                  }
                },
                response: {
                  "createdAtUnix": 1687369427,
                  "alertId": "1838382768383872"
                },
                attributes: [
                  {
                    type: "string",
                    desc: "Used to reference the alert.",
                    required: true,
                    name: "alertId",
                  },
                  {
                    type: "number",
                    desc: "Unix timestamp in seconds when the alert was created.",
                    required: true,
                    name: "createdAtUnix",
                  }
                ]
              },
              {
                title: "Getting an Alert",
                desc: "This endpoint simply retuens the properties of an Alert, along with the notifications the alert has triggered.",
                request: {
                  url: "https://campflare.com/v1/alerts/get?id={alertId}",
                  method: "GET",
                  headers: {
                    "accept": "application/json",
                    "authorization": "<your api key>",
                  }
                },
                parameters: [
                  {
                    name: "id",
                    type: "string",
                    desc: "The id of the Alert. This is the id of the alert that was returned when creating an Alert.",
                    required: true
                  }
                ],
                response: {
                    "alertId": "1685054675365138432",
                    "active": true,
                    "canceled": false,
                    "canceledAtUnix": 1690583423,
                    "campgrounds": [
                      "232451"
                    ],
                    "dates": [
                      "2023-08-03"
                    ],
                    "minimumNumberOfNights": 1,
                    "minimumRigLengthFt": 12,
                    "metadata": {
                      "anything": 123,
                      "you": "put",
                      "here": []
                    },
                    "notifications": [
                      {
                        "notificationId": "1685054761058963456",
                        "alertId": "1685054675365138432",
                        "sentAtUnix": 1690583336,
                        "site": "043: HIKE-IN",
                        "numberOfNights": 2,
                        "startDate": "2023-08-03",
                        "campgroundId": "232451",
                        "reserveLink": "https://campflare.com/r/232451",
                        "webhookStatusCode": "200",
                        "webhookStatus": "sent"
                      }
                    ]
                  },
                attributes: requestSchema
              },
              {
                title: "Stopping an Alert",
                desc: "This endpoint stops an Alert immediately. The alert will still be accessible via the get endpoint, however it will no longer send any notifications. An Alert can not be resumed once canceled.",
                request: {
                  parameters: [
                    {
                      name: "alertId",
                      type: "string",
                      desc: "The id of the Alert to cancel.",
                      required: true
                    },
                  ],
                  url: "https://api.campflare.com/v1/alerts/cancel",
                  method: "POST",
                  headers: {
                    "accept": "application/json",
                    "authorization": "<your api key>",
                    "Content-Type": "application/json"
                  },
                  body: {
                    "alertId": "1838382768383872"
                  }
                 },
                response: {
                  "canceledAtUnix": 1687369427,
                  "alertId": "1838382768383872"
                },
                attributes: [
                  {
                    type: "string",
                    desc: "Used to reference the Alert.",
                    required: true,
                    name: "alertId",
                  },
                  {
                    type: "number",
                    desc: "Unix timestamp in seconds when the Alert was canceled.",
                    required: true,
                    name: "createdAtUnix"
                  }
                ]
              }
            ]
          }
        },
        {
          title: "Webhooks",
          id: "introwduction 3",
          desc: "Campflare delivers notifications to your webhook endpoint instantly, which can be configured via the <a href='/platform/account'>account page</a>.",
          api: {
            requests: [
              {
                title: "Securing",
                type: "info",
                desc: `Webhooks are secured via a <a href='https://jwt.io/' target='blank'> JWT Token</a> (using <c>HS256</c>), sent from the server, that can be validated by the client. There are client libraries for JWTs in almost every language, with great documentation. <br>
                <br>The payload of the JWT will include 2 keys. 
                  <c>iat</c> is the time that the token was issued as a unix timestamp. And
                  <c>notificationId</c>, the id of the notification being sent in the webhook. 

                <br><br>

                The token will be sent in the <c>authorization</c> header. 

                <br><br> The shared secret to validate each token can be created on the <a href='/platform/account'>account page</a>.
                `,
              },
              {
                title: "Payload",
                desc: "The body of the webhook specifies the event, in this case <c>availabilityNotification</c> and the data associated with the notification.",
                request: {
                  url: "https://api.campflare.com/v1/alerts/create",
                  method: "POST",
                  headers: {
                    "accept": "application/json",
                    "authorization": "<jwt token>",
                    "Content-Type": "application/json"
                  },
                  parameters: [
                    
                  ],
                  body: {
                    "event": "availabilityNotification",
                    "data": {
                      "notification": {
                        "notificationId": "4827383327626",
                        "alertId": "1685054675365138432",
                        "sentAtUnix": 1687369123,
                        "site": "23",
                        "numberOfNights": 3,
                        "startDate": "2022-06-13",
                        "campgroundId": "38226",
                        "reserveLink": "https://campflare.com/r/232447"
                      },
                      "alertMetadata": {
                        "anything": 123,
                        "you": "want",
                        "here": []
                      }
                    }
                  }
                 },
                attributes: [
                  {
                    name: "event",
                    type: "string",
                    desc: "The type of event being sent to the webhook, in this case \"availabilityNotification\"",
                    required: true,
                  },
                  {
                    name: "data",
                    type: "object",
                    desc: "The data of the webhook.",
                    required: true,
                    attributes: [
                      {
                        name: "notification",
                        type: "object",
                        desc: "The data associated with the notification.",
                        required: true,
                        attributes: [
                          {
                            name: "id",
                            type: "string",
                            desc: "The id of the notification.",
                            required: true
                          },
                          {
                            name: "sentAtUnix",
                            type: "number",
                            desc: "The unix timestamp of when the notification was sent to the webhook.",
                            required: true
                          },
                          {
                            name: "site",
                            type: "string",
                            desc: "The site number/name that the availability was detected at.",
                            required: true
                          },
                          {
                            name: "numberOfNights",
                            type: "number",
                            desc: "The number of nights of availability.",
                            required: true
                          },
                          {
                            name: "startDate",
                            type: "string",
                            desc: "What date the availability starts at, in <c>YYYY-MM-DD</c> format.",
                            required: true
                          },
                          {
                            name: "campground",
                            type: "string",
                            desc: "The id of the campground the availability was detected at.",
                            required: true
                          },
                          {
                            name: "reserveLink",
                            type: "string",
                            desc: "The link to book the campground.",
                            required: true
                          }
                        ]
                      },
                      {
                        name: "alertMetadata",
                        type: "map",
                        desc: "The optional metadata that was created with the alert.",
                        required: false
                      },
                    ]
                  },
                ]
              }
            ]
          }
        },
      ]
    },  
  ]
} 


